import {
  Row,
  Col,
  Button,
  message,
  Form,
  Input,
  Select,
  Checkbox,
} from "antd";
import { IAdminUsers } from "common/interface/IAdminUsers";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAdminGroups, getAssignableGroups, getAdminUserDetail, getAllWhDivisions, getCities, getDarkStores, updateUser } from "store/admin-slice/adminServicesSlice";
import { fetchWarehouses } from "store/stock-exchange/stockExchangeSlice";
import { VEHICLE_TYPE_OPTIONS } from "common/constants";
import { parseStringOrList } from "common/utils";
import { MappedWarehousesDivisions, normalizeDivisionIds, pruneUnmappedWarehouses } from "./components/MappedWarehousesDivisions";

const ASSIGNABLE_ROLE_MIN_ID = 31;

const MESSAGE_KEY = "UPDATE_ADMIN_USER";
export const UpdateAdminUserPage = () => {
  const [currentAdminUser, setCurrentAdminUser] = useState<IAdminUsers>();
  useDocumentTitle("Update User");
  const { status, error, adminUserGroups, assignableGroups, darkStores, cities, allWhDivisions } = useAppSelector(
    (state) => state.adminServices
  );
  const { warehouses } = useAppSelector(
    (state) => state.stockExchangeServices
  );
  const sourceOptions = useAppSelector((state) =>
    parseStringOrList(state.app.initConfig?.source)
  );
  const designationOptions = useAppSelector((state) =>
    parseStringOrList(state.app.initConfig?.designation)
  );
  const shiftOptions = useAppSelector((state) =>
    parseStringOrList(state.app.initConfig?.shift)
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [selectedCity, setSelectedCity] = useState<number>();
  const { id } = useParams();

  const sourceSelectOptions = useMemo(() => {
    const fromConfig = sourceOptions.map((s) => ({ value: s, label: s }));
    const current = currentAdminUser?.source;
    if (current && !fromConfig.some((o) => o.value === current)) {
      return [{ value: current, label: current }, ...fromConfig];
    }
    return fromConfig;
  }, [sourceOptions, currentAdminUser?.source]);

  const designationSelectOptions = useMemo(() => {
    const fromConfig = designationOptions.map((d) => ({ value: d, label: d }));
    const current = currentAdminUser?.designation;
    if (current && !fromConfig.some((o) => o.value === current)) {
      return [{ value: current, label: current }, ...fromConfig];
    }
    return fromConfig;
  }, [designationOptions, currentAdminUser?.designation]);

  const assignableGroupIds = useMemo(
    () => new Set(assignableGroups.map(g => g.id)),
    [assignableGroups]
  );

  const groupSelectOptions = useMemo(
    () =>
      adminUserGroups
        .filter(g => g.id >= ASSIGNABLE_ROLE_MIN_ID)
        .map(g => ({
          value: g.id,
          label: g.name,
          disabled: !assignableGroupIds.has(g.id),
        })),
    [adminUserGroups, assignableGroupIds]
  );
  useEffect(() => {
    getUserDetails();
  }, []);

  const determineInitialCity = (mappedStores: number[]) => {
    if (mappedStores && mappedStores.length > 0) {
      const store = darkStores.find(store => store.id === mappedStores[0]);
      if (store) {
        setSelectedCity(store.cityId);
        return store.cityId;
      }
    }
    return null;
  };

  const getUserDetails = async () => {
    const temp = await dispatch(getAdminUserDetail(id!));
    if (temp.payload?.mappedStores?.length > 0) {
      const cityId = determineInitialCity(temp.payload.mappedStores);
      if (cityId) {
        dispatch(getDarkStores(cityId));
      }
    }
    setCurrentAdminUser(temp.payload);
  }

  const filteredStores = darkStores.filter(store => 
    selectedCity ? store.cityId === selectedCity : true
  );

  const handleCityChange = (value: number) => {
    setSelectedCity(value);
    // Clear mapped stores when city changes
    form.setFieldsValue({ mappedStores: [] });
    // Fetch stores for the selected city
    if (value) {
      dispatch(getDarkStores(value));
    }
  };

  useEffect(() => {
    dispatch(fetchWarehouses());
    dispatch(getAllWhDivisions());
  }, []);

  useEffect(() => {
    dispatch(getAdminGroups());
    dispatch(getAssignableGroups());
    dispatch(getCities());
    return () => {
      message.destroy();
    };
  }, []);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading User Group", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({
        content: "Admin User Updated!",
        key: MESSAGE_KEY,
      });
      form.resetFields();
      navigate(-1);
    } else if (status === "cancelling") {
      message.loading({
        content: "Cancelling User Update",
        key: MESSAGE_KEY, duration: 3.5
      });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY, duration: 3.5 });
    }
    else if (status === "saving") {
      message.loading({ content: "Saving User", key: MESSAGE_KEY });
    }
    return () => {
      message.destroy();
    };
  }, [status, error]);

  const [form] = Form.useForm();
  const onSave = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      // Every mapped warehouse must have at least one division; drop the ones
      // that don't and inform the user.
      const { mappedWh, mappedDivisions, removed } = pruneUnmappedWarehouses(
        values.mappedWh ?? [],
        values.mappedDivisions ?? [],
        warehouses,
        allWhDivisions
      );
      values.mappedWh = mappedWh;
      values.mappedDivisions = mappedDivisions;
      form.setFieldsValue({ mappedWh, mappedDivisions });
      if (removed.length > 0) {
        message.warning(
          `Removed warehouse(s) without a selected division: ${removed.join(", ")}`
        );
      }
      if (values.vehicleType == null) {
        values.vehicleType = null;
      }
      // Omitting / clearing source sends null so the backend clears the field.
      if (values.source == null) {
        values.source = null;
      }
      if (Array.from(currentAdminUser!['groups'].map((e: any) => e.name)).includes("administrator")) {
        if (!Array.from(values["groups"]).includes(1)) {
          values["groups"] = [...values["groups"], 1];
        }
      }
      // Select holds "true" / "false" strings; a cleared select is saved as false.
      values.enableLmPerformanceBasedPayout =
        values.enableLmPerformanceBasedPayout === "true";
      await dispatch(updateUser({ ...values, "id": id }));
    }
    catch (e) {
    }
  };

  const onCancelSave = () => {
    navigate(-1);
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Update Admin User" />
        </Col>
      </Row>

      {currentAdminUser === undefined ? <>Fetching User Details</> : <>
        <Form
          initialValues={{
            "firstname": currentAdminUser["firstname"],
            "lastname": currentAdminUser['lastname'],
            "email": currentAdminUser['email'],
            "phone": currentAdminUser['phone'].replace("+91", ""),
            "gender": currentAdminUser['gender'],
            "vehicleType": currentAdminUser['vehicleType'],
            "source": currentAdminUser['source'] ?? undefined,
            "designation": currentAdminUser['designation'] ?? undefined,
            "shift": currentAdminUser['shift'] ?? undefined,
            "enableLmPerformanceBasedPayout": currentAdminUser['enableLmPerformanceBasedPayout'] == null
                ? undefined
                : String(currentAdminUser['enableLmPerformanceBasedPayout']),
            "blocked": currentAdminUser['blocked'],
            "groups": (currentAdminUser['groups'] as any[]).map(e => e.id).filter((gId: number) => gId >= ASSIGNABLE_ROLE_MIN_ID),
            "mappedWh": ((currentAdminUser['mappedWh'] ?? []).map((e: any) => e.id)),
            "mappedDivisions": normalizeDivisionIds(currentAdminUser['mappedDivisions']),
            "mappedStores": currentAdminUser['mappedStores'] ?? [],
            "hasToBeGigWorker": currentAdminUser['hasToBeGigWorker'] ?? false,
            // "cityId": currentAdminUser['cityId'] ? [currentAdminUser['cityId']] : [], // Convert to array for multiple selection
          }}
          form={form}
          name="vendor-filter-form"
          layout="vertical"
          style={{ width: "50%" }}
        >
          <Form.Item name="firstname" label="First Name" rules={[
            {
              required: true,
            },
          ]}>
            <Input placeholder="First Name" size="small" allowClear required />
          </Form.Item>
          <Form.Item name="lastname" label="Last Name" rules={[
            {
              required: true,
            },
          ]}>
            <Input placeholder="Last Name" size="small" allowClear required />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[
            {
              required: true,
              type: "email",
              message: "Please enter a valid email",
            },
          ]}>
            <Input placeholder="Email" size="small" allowClear required />
          </Form.Item>
          <Form.Item name="phone" label="Phone" rules={[
            {
              pattern: new RegExp("^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$"),
              message: "Invalid phone number",
              min: 10,
              max: 10,
              required: true,
            },
          ]} >
            <Input
              placeholder="Phone"
              size="middle"
              maxLength={10}
              allowClear
            />
          </Form.Item>
          <Form.Item
            label="Gender"
            name="gender"
            required
          >
            <Select
              defaultValue="MALE"
              options={[
                {
                  value: 'MALE',
                  label: 'Male',
                }, {
                  value: 'FEMALE',
                  label: 'Female',
                },
              ]}
            />
          </Form.Item>
          
          <Form.Item
            label="Vehicle Type"
            name="vehicleType"
          >
            <Select
              placeholder="Select Vehicle Type"
              allowClear
              options={VEHICLE_TYPE_OPTIONS}
            />
          </Form.Item>

          <Form.Item
            label="Source"
            name="source"
          >
            <Select
              placeholder="Select Source"
              allowClear
              options={sourceSelectOptions}
            />
          </Form.Item>

          <Form.Item
            label="Designation"
            name="designation"
          >
            <Select
              placeholder="Select Designation"
              allowClear
              options={designationSelectOptions}
            />
          </Form.Item>

          <Form.Item
            label="Shift"
            name="shift"
          >
            <Select
              placeholder="Select Shift"
              allowClear
              options={shiftOptions.map((s) => ({ value: s, label: s }))}
            />
          </Form.Item>

          <Form.Item
            label="LM Performance Based Payout"
            name="enableLmPerformanceBasedPayout"
          >
            <Select
              placeholder="Select True / False"
              allowClear
              options={[
                { value: "true", label: "True" },
                { value: "false", label: "False" },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="Is Blocked"
            name="blocked"
            required
          >
            <Select
              options={[
                {
                  value: 0,
                  label: 'FALSE',
                }, {
                  value: 1,
                  label: 'TRUE',
                },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="City"
          >
            <Select
              value={selectedCity}
              onChange={handleCityChange}
              options={cities.map(city => ({
                value: city.id,
                label: `${city.name}, ${city.state}`,
              }))}
              allowClear
              placeholder="Select city to filter stores"
            />
          </Form.Item>

          <Form.Item
            label="Mapped Stores"
            name="mappedStores"
          >
            <Select
              mode="multiple"

              options={filteredStores.map(store => ({
                label: `${store.id} || ${store.name}`,
                value: store.id,
              }))}
            />
          </Form.Item>


          <MappedWarehousesDivisions
            form={form}
            warehouses={warehouses}
            divisions={allWhDivisions}
          />

          {/* <Form.Item
            label="Right Away Stores"
            name="rightAwayStores"
          >
            <Select
              mode="multiple"
              options={cities.map(city => ({
                label: <span>{city.name}, {city.state}</span>,
                title: `${city.name}, ${city.state}`,
                options: rightAwayStores.filter(store => store.cityId === city.id).map(store => ({ label: <span>{store.id} || {store.name}</span>, value: store.id })),
              })).filter(value => value.options.length !== 0)}
            />
          </Form.Item> */}

          <Form.Item name="hasToBeGigWorker" valuePropName="checked">
            <Checkbox>Has To Be Gig Worker</Checkbox>
          </Form.Item>

          <Form.Item name="groups" label="Groups">
            <Select
              mode="multiple"
              showSearch
              placeholder="Select roles"
              filterOption={(input, option) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={groupSelectOptions}
            />
          </Form.Item>
        </Form>
        <Row>
          <Col>
            <Button
              type="primary"
              onClick={onSave}
              loading={status === "loading"}
              disabled={status === "loading"}
            >
              Update User
            </Button>
          </Col>
          <Col style={{ justifyContent: "left", marginLeft: "20px" }}>
            <Button
              onClick={onCancelSave}
              disabled={status === "loading"}
            >
              Cancel
            </Button>
          </Col>
        </Row>
      </>}
    </>
  );
};